import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ModalOverlay, isModalOpen } from '../components/ModalOverlay';
import { selectedChannelUrl } from '../utils/selectedChannel';
import { STATUS_THEME_COLORS } from '../utils/statusColors';
import { shortcutKeyMatches } from '../utils/keyboard';
import { useUser } from '../providers/UserProvider';
import { useChannels } from '../providers/ChannelProvider';
import type { StoryDetail as StoryDetailType } from '../types';
import { StoryDetail } from '../components/StoryDetail';
import * as storyRepository from '../repositories/storyRepository';
import * as adminStoryRepository from '../repositories/adminStoryRepository';
import { statusEvent, statusFlagUpdate } from '../utils/userEvents';
import type { UserEventType } from '../utils/userEvents';
import { subscribeFeed } from '../repositories/feedRepository';

function getReaderChannelUrl() {
  return selectedChannelUrl();
}

function SubscribeFeedModal({
  feedUrl,
  feedTitle,
  domain,
  onClose,
  onSubscribed,
  refreshChannels,
}: {
  feedUrl: string;
  feedTitle?: string;
  domain?: string;
  onClose: () => void;
  onSubscribed: () => void;
  refreshChannels: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    setSaving(true);
    setError(null);
    try {
      await subscribeFeed(feedUrl, feedTitle || domain);
      await refreshChannels();
      onSubscribed();
    } catch (err) {
      setError((err as Error).message || 'Failed to subscribe');
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} className="subscribe-feed-modal">
      <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>Subscribe to feed</h3>
      <div style={{ fontSize: '0.88rem', color: 'var(--muted)', marginBottom: 16 }}>
        {feedTitle && <div style={{ marginBottom: 6 }}><strong>{feedTitle}</strong></div>}
        <div style={{ wordBreak: 'break-all' }}>{feedUrl}</div>
      </div>
      <p style={{ fontSize: '0.85rem', margin: '0 0 16px' }}>
        Articles from this feed will appear in your <strong>Subscriptions</strong> channel.
      </p>
      {error && <div style={{ color: STATUS_THEME_COLORS.error, fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="ghost-button" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="primary-button" onClick={handleSubscribe} disabled={saving}>
          {saving ? 'Subscribing...' : 'Subscribe'}
        </button>
      </div>
    </ModalOverlay>
  );
}

export default function ArticleReadPage() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useUser();
  const { refreshChannels } = useChannels();
  const [story, setStory] = useState<StoryDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const detailRef = useRef<HTMLElement>(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    setLoading(true);
    setError('');
    storyRepository.getStory(articleId)
      .then(async (s) => {
        setStory(s);
        setLoading(false);
        if (!s.is_read) {
          await storyRepository.recordEvent(s.id, 'read');
          setStory((prev) => prev ? { ...prev, is_read: true } : prev);
        }
      })
      .catch((err) => {
        setError((err as Error).message || 'Failed to load article');
        setLoading(false);
      });
  }, [articleId]);

  // Focus the article pane so spacebar/shift+space scrolls it
  useEffect(() => {
    if (!loading && story && detailRef.current) {
      detailRef.current.focus();
    }
  }, [loading, story]);

  const recordEvent = useCallback(async (eventType: UserEventType) => {
    if (!story) return;
    await storyRepository.recordEvent(story.id, eventType);
    setStory((prev) => {
      if (!prev) return prev;
      const update = statusFlagUpdate(eventType);
      return update ? { ...prev, ...update } : prev;
    });
  }, [story]);

  const handleToggleRead = useCallback(async () => {
    if (!story) return;
    await recordEvent(statusEvent('read', !story.is_read));
  }, [story, recordEvent]);

  const handleToggleFlag = useCallback(async () => {
    if (!story) return;
    await recordEvent(statusEvent('flag', !story.is_flagged));
  }, [story, recordEvent]);

  const handleReturnToFeed = useCallback(() => {
    if (story) {
      void storyRepository.recordEvent(story.id, 'return_to_feed');
    }
    navigate(getReaderChannelUrl());
  }, [story, navigate]);

  const handleFetch = async () => {
    if (!story) return;
    const updated = await adminStoryRepository.fetchStoryContent(story.id);
    setStory((prev) => {
      if (!prev) return prev;
      const patch: Partial<typeof prev> = { content: updated.content };
      if (updated.title) patch.title = updated.title;
      return { ...prev, ...patch };
    });
  };

  const handleEnrich = async () => {
    if (!story) return;
    await adminStoryRepository.enrichStory(story.id);
  };

  const handleSummaryUpdate = useCallback((newSummary: string) => {
    setStory((prev) => prev ? { ...prev, summary: newSummary } : prev);
  }, []);

  const handleTopicUpdate = useCallback((newTopic: string) => {
    setStory((prev) => prev ? { ...prev, topic: newTopic } : prev);
  }, []);

  // Keyboard: Escape goes back (only when no modal is open — modals handle their own ESC)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (isModalOpen()) return;
      // Don't hijack browser/OS shortcuts (Ctrl+F, Cmd+F, etc.)
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') {
        handleReturnToFeed();
      }
      if (shortcutKeyMatches(e, 'u')) {
        handleReturnToFeed();
      }
      if (shortcutKeyMatches(e, 'g') && story?.url) {
        window.open(story.url, '_blank', 'noreferrer');
        storyRepository.recordEvent(story.id, 'click_external');
      }
      if (shortcutKeyMatches(e, 'm')) handleToggleRead();
      if (shortcutKeyMatches(e, 'f')) handleToggleFlag();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    true,
    story,
    handleReturnToFeed,
    handleToggleRead,
    handleToggleFlag,
  ]);

  const showSubscribeIcon = story?.source_feed_url && !story.user_subscribed_to_feed;

  return (
    <div className="reader-shell">
      <nav className="article-read-navbar">
        <button className="article-read-back" onClick={handleReturnToFeed} title="Back to feed">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back
        </button>
        {story?.canonical_domain && (
          <span className="article-read-domain">
            {story.canonical_domain}
            {showSubscribeIcon && (
              <button
                onClick={() => setShowSubscribeModal(true)}
                title="Subscribe to this feed"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 0 0 6px',
                  color: 'var(--muted)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  verticalAlign: 'middle',
                  opacity: 0.6,
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.6'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 11a9 9 0 0 1 9 9" />
                  <path d="M4 4a16 16 0 0 1 16 16" />
                  <circle cx="5" cy="20" r="1" />
                </svg>
              </button>
            )}
          </span>
        )}
      </nav>
      <main className="article-read-main">
        {error ? (
          <div className="empty-panel">{error}</div>
        ) : (
          <StoryDetail
            ref={detailRef}
            story={story}
            loading={loading}
            isAdmin={isAdmin}
            onToggleRead={handleToggleRead}
            onToggleFlag={handleToggleFlag}
            onFetch={handleFetch}
            onEnrich={handleEnrich}
            onSummaryUpdate={handleSummaryUpdate}
            onTopicUpdate={handleTopicUpdate}
          />
        )}
      </main>
      {showSubscribeModal && story?.source_feed_url && (
        <SubscribeFeedModal
          feedUrl={story.source_feed_url}
          feedTitle={story.source_feed_title}
          domain={story.canonical_domain}
          refreshChannels={refreshChannels}
          onClose={() => setShowSubscribeModal(false)}
          onSubscribed={() => {
            setShowSubscribeModal(false);
            setStory((prev) => prev ? { ...prev, user_subscribed_to_feed: true } : prev);
          }}
        />
      )}
    </div>
  );
}
